import {
  AnimationEvent,
  CSSProperties,
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { isMobileOnly } from 'react-device-detect';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useDialogFocus } from '../../hooks';
import { isModalOpened } from '../../utils';
import { IconLabel } from '../IconLabel';

import classNames from 'classnames';
import classes from './Modal.module.scss';

export enum ModalButtonTypes {
  danger = 'danger',
  success = 'success',
}

interface ModalProps {
  isOpen: boolean;
  body: JSX.Element | null;
  onClose?: () => void;
  disableCloseByOverlay?: boolean;
  contentId?: string;
  contentStyle?: CSSProperties;
  previewMode?: boolean;
  previewSrc?: string;
  alignTop?: boolean;
  alignBottom?: boolean;
  fullScreen?: boolean;
  keepOpened?: boolean;
  opacityOverlay?: boolean;
  error?: string;
  root?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
}

export const Modal: FunctionComponent<ModalProps> = memo(
  ({
    isOpen,
    body,
    onClose,
    disableCloseByOverlay,
    contentId,
    contentStyle,
    previewMode,
    previewSrc,
    alignTop,
    alignBottom,
    fullScreen,
    keepOpened,
    opacityOverlay,
    error,
    root = 'modal-root',
    ariaLabel,
    ariaLabelledBy,
  }) => {
    const { t } = useTranslation();

    const [isMounted, setIsMounted] = useState(isOpen);

    const dialogRef = useRef<HTMLDivElement>(null);

    const contentRef = useRef<HTMLDivElement>(null);

    const onCloseHandler = useCallback(() => {
      if (keepOpened) {
        return;
      }

      onClose?.();

      if (!isModalOpened()) {
        document.body.style.overflow = 'auto';
      }
    }, [keepOpened, onClose]);

    useDialogFocus({
      dialogRef,
      isOpen: isOpen && isMounted,
      onEscape: onCloseHandler,
      initialFocusRef: contentRef,
      isModal: true,
    });

    useEffect(() => {
      if (isOpen) {
        setIsMounted(true);
      }
    }, [isOpen]);

    useEffect(() => {
      if (isMounted) {
        document.body.style.overflow = 'hidden';
      }

      return () => {
        if (!isModalOpened()) {
          document.body.style.overflow = 'auto';
        }
      };
    }, [isMounted]);

    const onContentAnimationEnd = useCallback(
      (event: AnimationEvent<HTMLDivElement>) => {
        if (isOpen || event.target !== event.currentTarget) {
          return;
        }
        setIsMounted(false);
      },
      [isOpen]
    );

    const copySourceClick = useCallback(async () => {
      if (!navigator.clipboard) {
        return;
      }

      await navigator.clipboard.writeText(previewSrc ?? '');

      toast(t('cardActions.source-url-copied'));
    }, [previewSrc, t]);

    if (!isMounted) {
      return null;
    }

    const isClosing = !isOpen;

    return ReactDOM.createPortal(
      <div
        ref={dialogRef}
        className={classNames(classes['modal'], { [classes['modal--closing']]: isClosing })}
        role={'dialog'}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
      >
        <div
          className={classNames(classes['modal__overlay'], {
            [classes['modal__overlay--preview-mode']]: previewMode,
            [classes['modal__overlay--opacity']]: opacityOverlay,
            [classes['modal__overlay--closing']]: isClosing,
          })}
          {...(!disableCloseByOverlay && { onClick: onCloseHandler })}
        ></div>
        <div
          id={contentId}
          ref={contentRef}
          className={classNames('modal-content', classes['modal__content'], {
            [classes['modal__content--preview-mode']]: previewMode,
            [classes['modal__content--align-top']]: alignTop,
            [classes['modal__content--align-bottom']]: alignBottom,
            [classes['modal__content--full-screen']]: fullScreen,
            [classes['modal__content--mobile']]: isMobileOnly,
            [classes['modal__content--closing']]: isClosing,
          })}
          style={contentStyle}
          tabIndex={-1}
          onAnimationEnd={onContentAnimationEnd}
        >
          {!previewMode && (
            <div className={classes['modal__header']}>
              {!keepOpened && (
                <button
                  type={'button'}
                  className={classes['modal__close']}
                  aria-label={t('common.close')}
                  onClick={onCloseHandler}
                />
              )}
            </div>
          )}

          <div
            className={classNames(classes['modal__body'], {
              [classes['modal__body--full-screen']]: fullScreen,
              [classes['modal__body--preview-mode']]: previewMode,
            })}
          >
            {body}
            {error && <div className={classes['modal__body-error']}>{error}</div>}
          </div>
        </div>

        {previewMode && (
          <>
            {Boolean(previewSrc) && (
              <IconLabel
                iconId={'copy'}
                iconSize={18}
                ariaLabel={t('cardActions.copy-source-url')}
                color={'#fff'}
                className={classes['modal__preview-src']}
                onClick={copySourceClick}
                singleColor
              />
            )}
            <button
              type={'button'}
              className={classNames(classes['modal__close'], classes['modal__close--preview-mode'])}
              aria-label={t('common.close')}
              onClick={onCloseHandler}
            />
          </>
        )}
      </div>,
      document.getElementById(root) as HTMLElement
    );
  }
);
